import { LIVE_MESSAGE_EVENT, LIVE_REACTION_EVENT } from '../../components/chat/events';
import { apiPost } from '../../lib/api_v2';
import { sendEchoMessage } from './send';
import {
  parseEchoLocalOptimisticMessageEventDetail,
  parseEchoSocketMessageEventDetail,
  parseEchoSocketReactionEventDetail,
  type EchoSocketMessageEventDetail,
  type EchoWireAction
} from './wire';
import { isRecord } from '../core/record';
import type {
  ChannelId,
  ClientRequestId,
  LocalOptimisticMessage,
  MessageId,
  MessageUpsertSyncEventType,
  ReactionDelta,
  SocketUnsubscribe,
  SyncEvent
} from '../core/types';

/** Minimal v2 transport surface used only for message sends. */
export interface EchoSocketTransport {
  post(path: string, json?: unknown, extra?: RequestInit): Promise<Response>;
}

/** Browser or test event target that carries live echo message/reaction CustomEvents. */
export interface EchoSocketEventTarget {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
}

/** Configures the socket adapter event source and send transport. */
export interface EchoSocketOptions {
  readonly eventTarget?: EchoSocketEventTarget;
  readonly transport?: Partial<EchoSocketTransport>;
}

/** Per-reaction live update emitted outside core SyncEvent until reaction merge semantics exist. */
export type EchoReactionDelta = ReactionDelta;

/** Locally-dispatched optimistic message surfaced separately from authoritative socket events. */
export interface EchoLocalOptimisticDelta {
  readonly message: LocalOptimisticMessage;
}

/** Bridges echo live events into channel-scoped core sync events and reaction deltas. */
export class EchoSocketAdapter {
  private readonly channelId: ChannelId;
  private readonly eventTarget: EchoSocketEventTarget;
  private readonly post: EchoSocketTransport['post'];
  private readonly messageSubscribers = new Set<(event: SyncEvent) => void>();
  private readonly reactionSubscribers = new Set<(delta: EchoReactionDelta) => void>();
  private readonly localOptimisticSubscribers = new Set<(delta: EchoLocalOptimisticDelta) => void>();
  private listening = false;

  constructor(channelId: ChannelId, options: EchoSocketOptions = {}) {
    this.channelId = channelId;
    this.eventTarget = options.eventTarget ?? defaultEventTarget();
    this.post = options.transport?.post ?? apiPost;
  }

  subscribe(cb: (event: SyncEvent) => void): SocketUnsubscribe {
    this.messageSubscribers.add(cb);
    this.ensureListening();

    return () => {
      this.messageSubscribers.delete(cb);
      this.detachIfIdle();
    };
  }

  /**
   * Reaction deltas stay outside core SyncEvent until the session wires per-reaction merge semantics.
   */
  onReactionDelta(cb: (delta: EchoReactionDelta) => void): SocketUnsubscribe {
    this.reactionSubscribers.add(cb);
    this.ensureListening();

    return () => {
      this.reactionSubscribers.delete(cb);
      this.detachIfIdle();
    };
  }

  onLocalOptimistic(cb: (delta: EchoLocalOptimisticDelta) => void): SocketUnsubscribe {
    this.localOptimisticSubscribers.add(cb);
    this.ensureListening();

    return () => {
      this.localOptimisticSubscribers.delete(cb);
      this.detachIfIdle();
    };
  }

  async send(clientRequestId: ClientRequestId, body: string): Promise<MessageId> {
    const message = await sendEchoMessage(this.channelId, this.post, clientRequestId, body);
    return message.id;
  }

  private readonly handleMessageEvent: EventListener = (event) => {
    const detail = customEventDetail(event);
    if (detail === undefined) return;

    // Local optimistic dispatches reuse LIVE_MESSAGE_EVENT with pseudotype rows and no
    // journal cursor; the session owns the optimistic path, so they must not re-enter as
    // server events (the sort_order cursor fallback would also poison sync positioning).
    if (isLocalPseudotypeDispatch(detail)) {
      const parsed = parseEchoLocalOptimisticMessageEventDetail(detail);
      if (!parsed.ok) return;
      if (parsed.value.message.channelId !== this.channelId) return;

      for (const subscriber of this.localOptimisticSubscribers) {
        subscriber({ message: parsed.value.message });
      }
      return;
    }
    const parsed = parseEchoSocketMessageEventDetail(detail);
    if (!parsed.ok) return;
    if (parsed.value.message.channelId !== this.channelId) return;

    const syncEvent = syncEventFromSocketDetail(parsed.value);
    for (const subscriber of this.messageSubscribers) {
      subscriber(syncEvent);
    }
  };

  private readonly handleReactionEvent: EventListener = (event) => {
    const detail = customEventDetail(event);
    if (detail === undefined) return;
    if (isLocalPseudotypeDispatch(detail)) return;

    const parsed = parseEchoSocketReactionEventDetail(detail);
    if (!parsed.ok) return;
    if (parsed.value.action === 'update') return;
    if (parsed.value.reaction.channelId !== this.channelId) return;

    for (const subscriber of this.reactionSubscribers) {
      subscriber({
        action: parsed.value.action,
        reaction: parsed.value.reaction
      });
    }
  };

  private ensureListening(): void {
    if (this.listening) return;
    this.eventTarget.addEventListener(LIVE_MESSAGE_EVENT, this.handleMessageEvent);
    this.eventTarget.addEventListener(LIVE_REACTION_EVENT, this.handleReactionEvent);
    this.listening = true;
  }

  private detachIfIdle(): void {
    if (
      !this.listening ||
      this.messageSubscribers.size > 0 ||
      this.reactionSubscribers.size > 0 ||
      this.localOptimisticSubscribers.size > 0
    ) {
      return;
    }
    this.eventTarget.removeEventListener(LIVE_MESSAGE_EVENT, this.handleMessageEvent);
    this.eventTarget.removeEventListener(LIVE_REACTION_EVENT, this.handleReactionEvent);
    this.listening = false;
  }
}

/** Maps parsed live message details to the core sync-event contract. */
export function syncEventFromSocketDetail(detail: EchoSocketMessageEventDetail): SyncEvent {
  const cursor = detail.cursor ?? detail.message.sortOrder;

  if (detail.action === 'destroy') {
    return {
      cursor,
      type: 'message.deleted',
      messageId: detail.message.id,
      sortOrder: detail.message.sortOrder,
      hardDeleted: true
    };
  }

  return {
    cursor,
    type: messageEventType(detail.action),
    messageId: detail.message.id,
    ...(detail.message.clientRequestId === undefined ? {} : { clientRequestId: detail.message.clientRequestId }),
    message: detail.message
  };
}

function messageEventType(action: Exclude<EchoWireAction, 'destroy'>): MessageUpsertSyncEventType {
  if (action === 'create') return 'message.created';
  return 'message.updated';
}

/** Returns the `detail` payload carried by a live CustomEvent, if any. */
export function customEventDetail(event: Event): unknown {
  return hasDetail(event) ? event.detail : undefined;
}

interface EventWithDetail extends Event {
  readonly detail: unknown;
}

function hasDetail(event: Event): event is EventWithDetail {
  return 'detail' in event;
}

/** Returns the window as the live-event target; throws outside a browser context. */
export function defaultEventTarget(): EchoSocketEventTarget {
  if (typeof window === 'undefined') {
    throw new Error('echo live events require a window event target outside tests');
  }
  return window;
}

/** Returns the raw message row carried by a LIVE_MESSAGE_EVENT detail, before wire parsing. */
export function rawLiveMessageRow(detail: unknown): Record<string, unknown> | undefined {
  if (typeof detail !== 'object' || detail === null) return undefined;
  if (!('data' in detail)) return undefined;
  return localDispatchRow(detail.data);
}

/** True when a LIVE_MESSAGE_EVENT detail is a locally-dispatched optimistic/placeholder row rather than a server broadcast. */
function isLocalPseudotypeDispatch(detail: unknown): boolean {
  const row = rawLiveMessageRow(detail);
  return Boolean(row && 'pseudotype' in row && row.pseudotype);
}

function localDispatchRow(data: unknown): Record<string, unknown> | undefined {
  if (typeof data !== 'object' || data === null) return undefined;
  if ('message' in data) {
    const message = data.message;
    if (isRecord(message)) return message;
  }
  return isRecord(data) ? data : undefined;
}
